import {produce} from 'immer';
import React from 'react';
import {ContentTitle} from 'src/design/atoms/Typography';
import {FormField} from 'src/app/forms/FormField';
import type {IFormFieldV2} from 'src/types/FormV2';
import {
  useGetSelectedField,
  useUpdateSelectedField,
} from '../../../hooks/useFormBuilder';

const assetTypeField: IFormFieldV2 = {
  name: 'rootAssetId',
  label: 'Asset Type',
  type: 'asset',
  description: 'Select the root asset type for this field.',
  assetOptions: {
    includeLeaves: false,
    requireLeaf: false,
  },
};

const showFullPathField: IFormFieldV2 = {
  name: 'showFullPath',
  label: 'Show Full Path',
  type: 'switch',
  description:
    'Show the full path of the asset below the selected asset. Otherwise, only the asset name is shown.',
};

export const AssetModalSettings = React.memo(() => {
  const field = useGetSelectedField();
  const updateSelectedField = useUpdateSelectedField();

  const handleAssetTypeChange = React.useCallback(
    (name: string, value: number) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          draft.assetOptions = {
            ...draft.assetOptions,
            rootAssetId: value,
          };
        })
      );
    },
    [field, updateSelectedField]
  );

  const handleShowFullPathChange = React.useCallback(
    (name: string, value: boolean) => {
      if (!field) return;
      updateSelectedField(
        produce(field, draft => {
          draft.assetOptions = {
            ...draft.assetOptions,
            showFullPath: value,
          };
        })
      );
    },
    [field, updateSelectedField]
  );

  if (!field || field.type !== 'asset') return null;
  return (
    <>
      <ContentTitle text="Asset Options" />
      <FormField
        field={assetTypeField}
        value={field.assetOptions?.rootAssetId}
        onChange={handleAssetTypeChange}
      />
      <FormField
        field={showFullPathField}
        value={field.assetOptions?.showFullPath}
        onChange={handleShowFullPathChange}
      />
    </>
  );
});
